import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, message, Input, Popconfirm, Row, Col } from 'antd';
import { EyeOutlined, PlusOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AddEvent from './AddEvent';
import '../../styles/App.css'; 

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventsSearchTerm, setEventsSearchTerm] = useState('');
  const [modalAddVisible, setModalAddVisible] = useState(false);
  const navigate = useNavigate(); 

  const fetchSavedEvents = async () => {
    setLoading(true);
    try {
      const savedEventsResponse = await axios.get('/events');
      setEvents(savedEventsResponse.data);
      setFilteredEvents(savedEventsResponse.data);
    } catch (error) {
      message.error('Error fetching saved events');
      console.error('Error fetching saved events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedEvents();
  }, []);

  const handleView = (event) => {
    navigate('/event-details', {
      state: { 
        event, 
        eventsList: filteredEvents 
      }
    });
  };

  const handleDelete = async (event) => {
    setLoading(true);
    try {
      await axios.delete(`/events/${event._id}`);
      message.success('Event deleted successfully');
      fetchSavedEvents();
    } catch (error) {
      console.error('Error deleting event:', error.message);
      message.error('Error deleting event');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchEvents = (e) => {
    const value = e.target.value;
    setEventsSearchTerm(value);

    const filtered = events.filter(event =>
      event.name.toLowerCase().includes(value.toLowerCase()) ||
      event.eventId.toString().includes(value) ||
      event.mnemonic.toLowerCase().includes(value.toLowerCase()) ||
      event.city.toLowerCase().includes(value.toLowerCase()) ||
      event.beginDate.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredEvents(filtered);
  };

  const columns = [
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      responsive: ['md', 'lg'],
      render: (text, record) => record.label[0]?.value || text,
    },
    {
      title: 'Mnemonic',
      dataIndex: 'mnemonic',
      key: 'mnemonic',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      responsive: ['md', 'lg'],
    },
    {
      title: 'Begin Date',
      dataIndex: 'beginDate',
      key: 'beginDate',
      sorter: (a, b) => new Date(a.beginDate) - new Date(b.beginDate),
      render: (text) => {
        return dayjs(text).format('DD-MM-YYYY');
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)} // Redirect to EventDetails.js
          />
          <Popconfirm
            title="Are you sure you want to delete this device config?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button style={{ color: 'red' }} icon={<DeleteOutlined />} />
          </Popconfirm>
          
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: '24px' }}>Event List</h1>
      <Row gutter={16} align="middle">
        <Col xs={16} sm={14} md={16}>
          <Input
            placeholder="Search by ID, name, mnemonic, city, or begin date"
            prefix={<SearchOutlined />}
            value={eventsSearchTerm}
            onChange={handleSearchEvents}
            style={{ marginBottom: '16px', width: '100%' }}
          />
        </Col>
        <Col xs={8} sm={10} md={8}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalAddVisible(true)}
            style={{ marginBottom: '16px', width: '100%' }}
          >
            <span className="hide-text-mobile">Add Event</span>
          </Button>
        </Col>
      </Row>
     
      <Table
        columns={columns}
        dataSource={filteredEvents}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
      />
      <AddEvent
        visible={modalAddVisible}
        onClose={() => setModalAddVisible(false)}
        fetchSavedEvents={fetchSavedEvents}
      />
    </div>
  );
};

export default EventList;
